import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import { TableHead } from '@mui/material'
import AddIcon from '../../../../assets/icons/Add.svg'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { SubjectState } from '../../../../features/types'

interface TablePaginationActionsProps {
    count: number
    page: number
    rowsPerPage: number
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme()
    const { count, page, rowsPerPage, onPageChange } = props

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0)
    }

    const handleBackButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, page - 1)
    }

    const handleNextButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, page + 1)
    }

    const handleLastPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
    }

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? (
                    <LastPageIcon />
                ) : (
                    <FirstPageIcon />
                )}
            </IconButton>

            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? (
                    <KeyboardArrowRight />
                ) : (
                    <KeyboardArrowLeft />
                )}
            </IconButton>

            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? (
                    <KeyboardArrowLeft />
                ) : (
                    <KeyboardArrowRight />
                )}
            </IconButton>

            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? (
                    <FirstPageIcon />
                ) : (
                    <LastPageIcon />
                )}
            </IconButton>
        </Box>
    )
}

interface Props {
    inactiveCourseSubjectsData?: SubjectState[]
    onDelete?: (subject: number) => void
}

const InactiveCourseSubjectsTable: React.FC<Props> = ({
    inactiveCourseSubjectsData = [],
}) => {
    const { formatMessage } = useIntl()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(5)
    const [createCourseSubject, { isLoading: isCreating }] =
        courseSubjectApiSlice.useCreateCourseSubjectMutation()
    const { courseId } = useParams()
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0
            ? Math.max(
                  0,
                  (1 + page) * rowsPerPage - inactiveCourseSubjectsData.length,
              )
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const addActiveSubject = async (subjectId: number) => {
        try {
            await createCourseSubject({ courseId, subjectId }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (error) {}
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>#</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'actions' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(rowsPerPage > 0
                        ? inactiveCourseSubjectsData.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage,
                          )
                        : inactiveCourseSubjectsData
                    ).map((row, index) => (
                        <TableRow key={row.id}>
                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {index + 1}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleKz}
                            </TableCell>

                            <TableCell
                                component="th"
                                scope="row"
                            >
                                {row?.titleRu}
                            </TableCell>

                            <TableCell
                                style={{ width: 60 }}
                                align="right"
                            >
                                <IconButton
                                    size="small"
                                    disabled={isCreating}
                                    onClick={() => addActiveSubject(row.id)}
                                >
                                    <img
                                        src={AddIcon}
                                        alt="add"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={6} />
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={5}
                            count={inactiveCourseSubjectsData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default InactiveCourseSubjectsTable
